import { ReactNode, useContext, useRef } from 'react';
import { Box, Button, Dialog, IconButton, Stack, Typography, useMediaQuery } from '@mui/material';
import AccountBalance from '@mui/icons-material/AccountBalance';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import CloudOutlined from '@mui/icons-material/CloudOutlined';
import GroupAdd from '@mui/icons-material/GroupAdd';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Close from '@mui/icons-material/Close';
import { AUTH_TOKENS } from '../../pages/signup-login/auth/auth-branding';
import { CustomThemeContext } from '../../../context/theme/custom-theme-context';
import { GET_STARTED_STEPS, GetStartedStepId } from '../../../hooks/use-get-started-steps';
import { WelcomeBrandBand } from './welcome-brand-band';
import { WelcomeStepCard } from './welcome-step-card';
import { BRAND_DISPLAY_FONT, GREEN_PALE_LIGHT, rise, WELCOME_DELAYS, WELCOME_KEYFRAMES } from './welcome-animations';

const STEP_ICONS: Record<GetStartedStepId, ReactNode> = {
  'company-details': <AccountBalance sx={{ fontSize: 24 }} />,
  'reporting-period': <CalendarMonth sx={{ fontSize: 24 }} />,
  'first-location': <PlaceOutlined sx={{ fontSize: 24 }} />,
  'invite-colleague': <GroupAdd sx={{ fontSize: 24 }} />,
  'first-emission': <CloudOutlined sx={{ fontSize: 24 }} />,
};

interface WelcomeDialogProps {
  open: boolean;
  /** Personalises the heading; falls back to a generic greeting when absent. */
  firstName?: string;
  /** Close the dialog (flip `open` to false in the parent). */
  onDismiss: () => void;
  /** Navigate to an onboarding destination (a step's deep link). */
  onNavigate: (path: string) => void;
  /** Open the dashboard's existing "start a reporting period" dialog. */
  onStartReportPeriod: () => void;
  /** Reveal and scroll to the dashboard's Get-started guide. */
  onShowGetStarted: () => void;
}

/**
 * First-run welcome modal shown once after registering/logging in, until the org
 * has a reporting period. Bespoke two-column marketing dialog (brand band +
 * onboarding steps) recreated from the hifi handoff, deliberately not the
 * standard form `DialogWrapper`.
 *
 * The step list renders from GET_STARTED_STEPS, the same definitions that drive
 * the dashboard's persistent setup guide, and the primary CTA hands over to
 * that guide: the dialog is the flourish, the guide is the checklist that
 * survives a refresh.
 *
 * Handoff behaviour: every action fades the dialog out first, then runs. The
 * pending action is stored and executed on the MUI exit transition's `onExited`,
 * so navigation (or opening the report-period dialog) happens after the welcome
 * has visibly left, with no two-dialog overlap.
 */
export const WelcomeDialog = ({ open, firstName, onDismiss, onNavigate, onStartReportPeriod, onShowGetStarted }: WelcomeDialogProps) => {
  const prefersReduced = useMediaQuery('(prefers-reduced-motion: reduce)');
  const { partnerName } = useContext(CustomThemeContext);
  const pendingAction = useRef<(() => void) | null>(null);

  // Store the action, then close: it runs once the exit transition completes.
  const choose = (action: (() => void) | null) => {
    pendingAction.current = action;
    onDismiss();
  };

  const handleExited = () => {
    const action = pendingAction.current;
    pendingAction.current = null;
    action?.();
  };

  const stepAction = (id: GetStartedStepId): (() => void) =>
    id === 'reporting-period'
      ? onStartReportPeriod
      : () => onNavigate(GET_STARTED_STEPS.find((step) => step.id === id)!.path);

  return (
    <Dialog
      open={open}
      onClose={() => choose(null)}
      maxWidth={false}
      transitionDuration={prefersReduced ? 0 : { enter: 320, exit: 450 }}
      TransitionProps={{ onExited: handleExited }}
      slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(17,30,33,0.5)', backdropFilter: 'blur(2px)' } } }}
      PaperProps={{
        sx: {
          width: 760,
          maxWidth: '96vw',
          m: 2,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          borderRadius: '20px',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: AUTH_TOKENS.border,
          boxShadow: '0 30px 80px rgba(17,30,33,.34)',
          animation: prefersReduced ? 'none' : 'wdIn .5s cubic-bezier(.2,.8,.2,1) both',
          ...WELCOME_KEYFRAMES,
          ...(prefersReduced && { '&, & *': { animation: 'none !important' } }),
        },
      }}
    >
      <WelcomeBrandBand />

      {/* Right content column; scrolls on short viewports so the five steps stay reachable. */}
      <Box sx={{ position: 'relative', flex: 1, minWidth: 0, p: '32px 40px 30px', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        <IconButton
          onClick={() => choose(null)}
          aria-label="Dismiss"
          sx={{ position: 'absolute', top: 16, right: 16, width: 32, height: 32, color: AUTH_TOKENS.inkTertiary, '&:hover': { bgcolor: '#f1f1f1' } }}
        >
          <Close sx={{ fontSize: 22 }} />
        </IconButton>

        {/* Success chip */}
        <Box
          sx={{
            display: 'inline-flex',
            alignSelf: 'flex-start',
            alignItems: 'center',
            gap: '7px',
            p: '6px 13px',
            borderRadius: '100px',
            bgcolor: GREEN_PALE_LIGHT,
            color: 'primary.dark',
            animation: rise(WELCOME_DELAYS.chip),
          }}
        >
          <CheckCircle sx={{ fontSize: 17 }} />
          <Typography component="span" sx={{ fontSize: 13, fontFamily: BRAND_DISPLAY_FONT }}>
            Account created
          </Typography>
        </Box>

        <Typography
          component="h2"
          sx={{ mt: '16px', fontSize: 27, fontFamily: BRAND_DISPLAY_FONT, letterSpacing: '-.015em', lineHeight: 1.14, color: 'text.primary', animation: rise(WELCOME_DELAYS.heading) }}
        >
          {firstName ? `Welcome to ${partnerName}, ${firstName}` : `Welcome to ${partnerName}`}
        </Typography>

        <Typography
          sx={{ mt: '8px', fontSize: 14.5, lineHeight: 1.55, color: AUTH_TOKENS.inkSecondary, animation: rise(WELCOME_DELAYS.subcopy) }}
        >
          Your workspace is ready. These steps take you to your first recorded emissions, and they stay
          in the setup guide on your dashboard until each one is done.
        </Typography>

        <Stack spacing={1.25} sx={{ mt: '20px' }}>
          {GET_STARTED_STEPS.map((step, index) => (
            <WelcomeStepCard
              key={step.id}
              icon={STEP_ICONS[step.id]}
              title={step.label}
              subtitle={step.description}
              onClick={() => choose(stepAction(step.id))}
              sx={{ p: '12px 16px', animation: rise(WELCOME_DELAYS.cards[index] ?? WELCOME_DELAYS.cards[WELCOME_DELAYS.cards.length - 1]) }}
            />
          ))}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2.5} sx={{ mt: '22px', animation: rise(WELCOME_DELAYS.action) }}>
          <Button
            onClick={() => choose(onShowGetStarted)}
            startIcon={<CheckCircle sx={{ fontSize: 20 }} />}
            disableElevation
            sx={{ py: '13px', px: '22px', fontSize: 15, fontFamily: BRAND_DISPLAY_FONT, '&:hover': { bgcolor: 'primary.dark' } }}
          >
            Open the setup guide
          </Button>
          <Button
            onClick={() => choose(null)}
            variant="text"
            sx={{ fontSize: 14.5, fontFamily: BRAND_DISPLAY_FONT, color: 'primary.main', bgcolor: 'transparent', '&:hover': { color: 'primary.dark', bgcolor: 'transparent' } }}
          >
            Look around first
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};
